import type { JSX } from 'react';

import { Palette } from 'DesignSystem2/Theme';

import SvgText from '../SvgText';

/**
 * Vertical clearance the date label occupies below `barTopY`. The bar starts here, and
 * callers that pass `hideLine` to draw their own styled bar should start it here too so
 * it never runs through the label.
 */
export const DATE_LINE_LABEL_CLEARANCE = 20;

interface DateLineProps {
    xDefault: number;
    xMin: number;
    xMax: number;
    barTopY: number;
    barBottomY: number;
    date: Date;
    data?: string;
    formatOptions?: Intl.DateTimeFormatOptions;
    hideLine?: boolean;
    backgroundColor?: string;
}

const DateLine = ({
    xDefault,
    xMin,
    xMax,
    barTopY,
    barBottomY,
    date,
    data,
    formatOptions = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' },
    hideLine = false,
    backgroundColor,
}: DateLineProps): JSX.Element => {
    // Use toLocaleString (not toLocaleDateString) so the formatOptions's hour/minute
    // fields actually render — toLocaleDateString silently drops time components.
    const dateString = data ?? date?.toLocaleString('en-US', formatOptions)?.toUpperCase() ?? '';

    const textWidth = dateString.length * 7.5;
    const x = Math.max(Math.min(xMax - textWidth / 2, xDefault), xMin + textWidth / 2);

    // Use provided background color or fall back to CSS variable
    const strokeColor = backgroundColor || 'var(--surface-x10)';

    return (
        <>
            <SvgText
                dx={x}
                dy={barTopY + 12}
                className="text-[13px] leading-[20px] font-medium tracking-[0.13px] stroke-[4px] pointer-events-none"
                textAnchor="middle"
                style={{
                    alignmentBaseline: 'middle',
                    paintOrder: 'stroke',
                    stroke: strokeColor,
                }}
                fill={Palette.Text.x20}
            >
                {dateString}
            </SvgText>
            {!hideLine && (
                <line
                    x1={xDefault}
                    y1={barTopY + DATE_LINE_LABEL_CLEARANCE}
                    x2={xDefault}
                    y2={barBottomY}
                    stroke={Palette.Fill.x20}
                    strokeWidth={2}
                    pointerEvents="none"
                />
            )}
        </>
    );
};

export default DateLine;
